import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { User } from '../../models/user.model';
import { Invitation } from '../../models/invitation.model';
import { firstValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { CurrentAccountResponse, TenantMembershipRole } from '@tangible/shared';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private http = inject(HttpClient);

  private _user: WritableSignal<User | null> = signal(null);
  public user = this._user.asReadonly();
  private loadCurrentUserPromise: Promise<User | null> | null = null;

  /** Signed-in account, with membership and member only when it has tenant access. */
  async fetchCurrentAccount(): Promise<CurrentAccountResponse> {
    return CurrentAccountResponse.parse(
      await firstValueFrom(this.http.get<unknown>('/auth/current-account/')),
    );
  }

  async getCurrentUser(): Promise<User> {
    const { account, membership, member } = await this.fetchCurrentAccount();
    if (!membership || !member) {
      throw new Error('No membership for this tenant.');
    }
    const user: User = {
      ...member,
      email: account.email,
      role: membership.role,
    };
    this._user.set(user);
    return user;
  }

  async loadCurrentUser(): Promise<User | null> {
    if (this._user()) {
      return this._user();
    }

    if (this.loadCurrentUserPromise) {
      return this.loadCurrentUserPromise;
    }

    this.loadCurrentUserPromise = this.getCurrentUser()
      .then((user) => user)
      .catch(() => {
        this._user.set(null);
        return null;
      })
      .finally(() => {
        this.loadCurrentUserPromise = null;
      });

    return this.loadCurrentUserPromise;
  }

  redirectToLogin(
    nextPath = window.location.pathname + window.location.search,
  ) {
    const params = new URLSearchParams({ next: nextPath });
    window.location.assign(`/auth/tenant-login/?${params.toString()}`);
  }

  async logout(): Promise<void> {
    window.location.assign('/auth/tenant-logout/');
    this._user.set(null);
  }

  // Tenant members
  getUsers$(): Observable<User[]> {
    return this.http
      .get<unknown>('/members-api/members/')
      .pipe(map((raw) => User.array().parse(raw)));
  }

  async patchUser$(user: User, patch: object) {
    return User.parse(
      await firstValueFrom(
        this.http.patch<unknown>(`/members-api/members/${user.id}/`, patch),
      ),
    );
  }

  async deleteUser$(user: User) {
    return await firstValueFrom(
      this.http.delete<void>(`/members-api/members/${user.id}/`),
    );
  }

  // Invitations
  getInvitations$(): Observable<Invitation[]> {
    return this.http.get<Invitation[]>(`/members-api/invitations/`);
  }

  createInvitation$(email: string, role: TenantMembershipRole) {
    return this.http.post<Invitation>(`/members-api/invitations/`, {
      email,
      role,
    });
  }

  resendInvitation$(token: string) {
    return this.http.post<Invitation>(
      `/members-api/invitations/${token}/resend/`,
      {},
    );
  }

  revokeInvitation$(token: string) {
    return this.http.delete<Invitation>(`/members-api/invitations/${token}/`);
  }

  constructor() {
    void this.loadCurrentUser();
  }
}
